import { lazy, Suspense } from "react";
import Header from "@/components/Header";
import StickyCallBar from "@/components/StickyCallBar";
import {
  BUSINESS,
  MEDIA,
  CLEANING_TYPES,
  CLEANING_FREQUENCIES,
  CLEANING_WHATS_INCLUDED,
  CLEANING_WHY_US,
} from "@/lib/constants";
import {
  ArrowRight,
  Phone,
  Sparkles,
  ShieldCheck,
  CheckCircle2,
  Star,
  Home,
  Building2,
  KeyRound,
  Wand2,
  PartyPopper,
} from "lucide-react";

const QuoteForm = lazy(() => import("@/components/QuoteForm"));
const Footer = lazy(() => import("@/components/Footer"));

const SectionFallback = () => (
  <div className="min-h-[240px] w-full" aria-hidden="true" />
);

const TYPE_ICONS = {
  residential: Home,
  commercial: Building2,
  "move-in-out": KeyRound,
  "deep-clean": Wand2,
  "party-cleanup": PartyPopper,
};

export default function Cleaning() {
  const scrollToQuote = () => {
    const el = document.getElementById("quote");
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <div data-testid="cleaning-page" className="bg-[#0a1210] text-white overflow-x-hidden">
      <Header onQuoteClick={scrollToQuote} />
      <main>
        <CleaningHero onQuoteClick={scrollToQuote} />
        <TypesSection />
        <IncludedSection />
        <FrequencySection onQuoteClick={scrollToQuote} />
        <WhyUsSection />
        <Suspense fallback={<SectionFallback />}>
          <QuoteForm
            source="Cleaning Service"
            heading={
              <>
                Request your free
                <br />
                <span className="text-[#8AB273]">cleaning</span> quote.
              </>
            }
            intro="Tell us about your home or office and we'll come back with a no-obligation cleaning quote, usually same day."
          />
          <Footer />
        </Suspense>
      </main>
      <StickyCallBar />
    </div>
  );
}

function CleaningHero({ onQuoteClick }) {
  return (
    <section
      data-testid="cleaning-hero"
      className="relative min-h-[92vh] w-full overflow-hidden"
    >
      <div className="absolute inset-0">
        <img
          src={MEDIA.cleaningHero}
          srcSet={`${MEDIA.cleaningHero}&w=800 800w, ${MEDIA.cleaningHero}&w=1600 1600w, ${MEDIA.cleaningHero}&w=2200 2200w`}
          sizes="100vw"
          alt=""
          loading="eager"
          fetchPriority="high"
          decoding="async"
          width="2200"
          height="1400"
          className="h-full w-full object-cover scale-105"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-black/55 to-[#0a1210]" />
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_left,rgba(115,212,74,0.16),transparent_55%)]" />
      </div>

      <div className="relative z-10 mx-auto max-w-7xl px-5 lg:px-10 pt-36 lg:pt-44 pb-24">
        <div className="grid lg:grid-cols-12 gap-10 items-end">
          <div className="lg:col-span-8">
            <div className="ppc-reveal ppc-d-1 inline-flex items-center gap-2 px-3 py-1.5 rounded-full ppc-glass">
              <Sparkles size={14} className="text-[#8AB273]" />
              <span className="label-eyebrow text-[#8AB273]">
                Cleaning by Premium Property Care
              </span>
            </div>

            <h1
              data-testid="cleaning-headline"
              className="font-display ppc-reveal ppc-d-2 mt-8 text-5xl sm:text-6xl lg:text-7xl xl:text-[88px] font-extrabold leading-[0.95] tracking-tighter"
            >
              Spotless
              <br />
              <span className="text-[#8AB273]">Results,</span>
              <br />
              <span className="text-white/60">Every Visit.</span>
            </h1>

            <p className="ppc-reveal ppc-d-3 mt-8 text-lg lg:text-xl text-white/75 max-w-2xl leading-relaxed">
              Delivering top-tier residential and commercial cleaning across
              the Niagara Region for 10+ years. From recurring weekly visits
              to full deep-clean overhauls, our dedicated cleaning specialists
              provide a reliable, spotless finish on every visit. Same
              consistent crew. No hassle. No surprises.
            </p>

            <div className="ppc-reveal ppc-d-4 mt-10 flex flex-col sm:flex-row gap-3">
              <button
                data-testid="cleaning-hero-quote-btn"
                onClick={onQuoteClick}
                className="group inline-flex items-center justify-center gap-2 bg-[#8AB273] text-black font-semibold px-6 py-4 rounded-full hover:bg-[#9DC585] transition-all"
              >
                Get my free cleaning quote
                <ArrowRight
                  size={18}
                  className="group-hover:translate-x-1 transition-transform"
                />
              </button>
              <a
                href={BUSINESS.phoneHref}
                data-testid="cleaning-hero-call-btn"
                className="inline-flex items-center justify-center gap-2 ppc-glass px-6 py-4 rounded-full text-white font-medium hover:bg-white/5 transition"
              >
                <Phone size={16} className="text-[#8AB273]" />
                Call {BUSINESS.phone}
              </a>
            </div>

            <div className="ppc-reveal ppc-d-5 mt-10 flex flex-wrap gap-x-7 gap-y-3 text-sm text-white/55">
              <span className="inline-flex items-center gap-2">
                <ShieldCheck size={15} className="text-[#8AB273]" /> Insured &
                bonded
              </span>
              <span>10+ years experience</span>
              <span>Residential + Commercial</span>
              <span>Eco-friendly options</span>
            </div>
          </div>

          {/* Trust card */}
          <div className="lg:col-span-4 ppc-reveal ppc-d-4">
            <div className="ppc-glass ppc-glow rounded-3xl p-7 lg:p-8">
              <div className="label-eyebrow">Trusted since 2014</div>
              <div className="mt-4 flex items-baseline gap-2">
                <span className="font-display text-6xl lg:text-7xl font-extrabold tracking-tighter text-white">
                  10+
                </span>
                <span className="text-white/50 text-sm">years serving Niagara</span>
              </div>
              <p className="mt-3 text-white/60 text-sm leading-relaxed">
                Our cleaning team has been keeping homes and businesses across
                the region spotless for over a decade. Same-crew continuity,
                real accountability.
              </p>
              <div className="my-6 h-px w-full bg-white/10" />
              <div className="flex items-center gap-3">
                <div className="flex">
                  {Array.from({ length: 5 }).map((_, i) => (
                    <Star key={i} size={16} className="text-[#8AB273] fill-[#8AB273]" />
                  ))}
                </div>
                <span className="text-sm text-white/70">
                  5.0 from local clients
                </span>
              </div>
              <button
                data-testid="cleaning-hero-cta-2"
                onClick={onQuoteClick}
                className="mt-6 w-full bg-white text-black font-semibold py-3 rounded-full hover:bg-white/90 transition"
              >
                Book a cleaning quote
              </button>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function TypesSection() {
  return (
    <section
      id="cleaning-types"
      data-testid="cleaning-types"
      className="relative py-24 lg:py-32 border-t border-white/5"
    >
      <div className="mx-auto max-w-7xl px-5 lg:px-10">
        <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-6 mb-14">
          <div className="max-w-2xl">
            <span className="label-eyebrow">— What we clean</span>
            <h2 className="font-display mt-4 text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tighter leading-[1]">
              Homes, offices,
              <br />
              <span className="text-[#8AB273]">and everything in between.</span>
            </h2>
          </div>
          <p className="text-white/60 max-w-md lg:text-right">
            Every job is quoted the same way — walk-through first, no surprise
            add-ons, and the same crew every visit.
          </p>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5 lg:gap-6">
          {CLEANING_TYPES.map((t) => {
            const Icon = TYPE_ICONS[t.key];
            return (
              <article
                key={t.key}
                data-testid={`cleaning-type-${t.key}`}
                className="group relative overflow-hidden rounded-3xl border border-white/10 min-h-[320px]"
              >
                <img
                  src={t.photo}
                  alt={t.title}
                  loading="lazy"
                  decoding="async"
                  width="1400"
                  height="1000"
                  className="absolute inset-0 h-full w-full object-cover scale-105 group-hover:scale-110 transition-transform duration-[1100ms]"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black via-black/50 to-black/10" />
                {t.priceFrom && (
                  <div
                    className="absolute top-5 right-5 inline-flex items-center px-3 py-1.5 rounded-full bg-[#8AB273] text-black text-[11px] font-bold uppercase tracking-[0.14em]"
                    data-testid={`cleaning-price-${t.key}`}
                  >
                    {t.priceFrom}
                  </div>
                )}
                <div className="relative h-full p-6 lg:p-8 flex flex-col justify-end">
                  <div className="h-11 w-11 rounded-xl bg-[#8AB273] text-black flex items-center justify-center mb-4">
                    {Icon && <Icon size={20} />}
                  </div>
                  <h3 className="font-display text-2xl lg:text-3xl font-bold tracking-tight">
                    {t.title}
                  </h3>
                  <p className="text-sm text-white/75 mt-2 max-w-md leading-relaxed">
                    {t.blurb}
                  </p>
                </div>
              </article>
            );
          })}
        </div>
      </div>
    </section>
  );
}

function IncludedSection() {
  return (
    <section
      data-testid="cleaning-included"
      className="relative py-24 lg:py-32 bg-[#0d1614] border-t border-white/5"
    >
      <div className="mx-auto max-w-7xl px-5 lg:px-10 grid lg:grid-cols-12 gap-10 lg:gap-16 items-center">
        <div className="lg:col-span-5 relative order-2 lg:order-1">
          <div className="rounded-3xl overflow-hidden border border-white/10">
            <img
              src={MEDIA.cleaningVacuum}
              alt="Professional cleaner vacuuming a modern living room"
              loading="lazy"
              decoding="async"
              width="1600"
              height="1200"
              className="w-full h-auto object-cover"
            />
          </div>
        </div>
        <div className="lg:col-span-7 order-1 lg:order-2">
          <span className="label-eyebrow">— What&apos;s included</span>
          <h2 className="font-display mt-4 text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tighter leading-[1]">
            No corners cut.
            <br />
            <span className="text-[#8AB273]">Literally.</span>
          </h2>
          <p className="mt-6 text-white/65 leading-relaxed max-w-xl">
            Every standard clean follows the same detailed checklist. Add
            extras like inside-fridge, oven, or window washing at the walk-through.
          </p>

          <ul className="mt-8 grid sm:grid-cols-2 gap-x-6 gap-y-3">
            {CLEANING_WHATS_INCLUDED.map((i) => (
              <li
                key={i}
                className="flex gap-2.5 text-sm text-white/80"
                data-testid={`cleaning-include-${i.toLowerCase().slice(0, 14).replace(/[^a-z0-9]+/g, "-")}`}
              >
                <CheckCircle2
                  size={17}
                  className="text-[#8AB273] flex-none mt-0.5"
                />
                <span>{i}</span>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </section>
  );
}

function FrequencySection({ onQuoteClick }) {
  return (
    <section
      data-testid="cleaning-frequency"
      className="relative py-24 lg:py-32 border-t border-white/5"
    >
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_bottom_right,rgba(115,212,74,0.08),transparent_55%)] pointer-events-none" />
      <div className="relative mx-auto max-w-7xl px-5 lg:px-10">
        <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-6 mb-14">
          <div className="max-w-2xl">
            <span className="label-eyebrow">— Frequency</span>
            <h2 className="font-display mt-4 text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tighter leading-[1]">
              How often should
              <br />
              <span className="text-[#8AB273]">we visit?</span>
            </h2>
          </div>
          <p className="text-white/60 max-w-md lg:text-right">
            Pick a rhythm that fits — we&apos;ll lock in your rate and same-crew
            slot. Final pricing after a free walk-through.
          </p>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-5 lg:gap-6">
          {CLEANING_FREQUENCIES.map((f) => (
            <article
              key={f.key}
              data-testid={`cleaning-freq-${f.key}`}
              className={`relative rounded-3xl p-7 flex flex-col transition-all hover:-translate-y-1 ${
                f.featured
                  ? "border-2 border-[#8AB273]/50 bg-gradient-to-br from-[#172621] to-[#0a1210] ppc-glow"
                  : "border border-white/10 bg-[#131c17]"
              }`}
            >
              {f.featured && (
                <div className="absolute -top-3 left-7 inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-[#8AB273] text-black text-[10px] font-bold uppercase tracking-[0.18em]">
                  Best value
                </div>
              )}
              <div className="label-eyebrow text-white/55">{f.sub}</div>
              <h3 className="font-display mt-3 text-3xl font-extrabold tracking-tighter">
                {f.title}
              </h3>
              <ul className="mt-5 space-y-2.5 flex-1">
                {f.perks.map((p) => (
                  <li key={p} className="flex gap-2 text-sm text-white/80">
                    <span className="text-[#8AB273]">✓</span>
                    {p}
                  </li>
                ))}
              </ul>
              <button
                onClick={onQuoteClick}
                data-testid={`cleaning-freq-cta-${f.key}`}
                className={`mt-6 px-4 py-3 rounded-full font-semibold text-sm transition ${
                  f.featured
                    ? "bg-[#8AB273] text-black hover:bg-[#9DC585]"
                    : "ppc-glass text-white hover:bg-white/5"
                }`}
              >
                Get quote
              </button>
            </article>
          ))}
        </div>
      </div>
    </section>
  );
}

function WhyUsSection() {
  return (
    <section
      data-testid="cleaning-why-us"
      className="relative py-24 lg:py-32 bg-[#0d1614] border-t border-white/5 overflow-hidden"
    >
      <div className="absolute inset-0">
        <img
          src={MEDIA.cleaningPortrait}
          alt=""
          aria-hidden="true"
          loading="lazy"
          decoding="async"
          width="1600"
          height="1100"
          className="h-full w-full object-cover opacity-20"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-[#0a1210] via-[#0a1210]/85 to-transparent" />
      </div>
      <div className="relative mx-auto max-w-7xl px-5 lg:px-10">
        <div className="max-w-2xl">
          <span className="label-eyebrow">— Why our crew</span>
          <h2 className="font-display mt-4 text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tighter leading-[1]">
            10+ years strong.
            <br />
            <span className="text-[#8AB273]">Same crew, every visit.</span>
          </h2>
          <p className="mt-6 text-white/70 leading-relaxed max-w-xl">
            Our cleaners are professional, insured, and have been working
            together for over a decade. That&apos;s the difference between a
            place that&apos;s tidied and a place that feels new.
          </p>
        </div>

        <div className="mt-12 grid grid-cols-2 lg:grid-cols-4 gap-4">
          {CLEANING_WHY_US.map((s) => (
            <div
              key={s.label}
              data-testid={`cleaning-stat-${s.label.toLowerCase().replace(/\s+/g, "-")}`}
              className="ppc-glass rounded-2xl p-5"
            >
              <div className="font-display text-4xl lg:text-5xl font-extrabold tracking-tighter text-[#8AB273]">
                {s.stat}
              </div>
              <div className="text-xs text-white/55 mt-2">{s.label}</div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
